import { Component, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ColumnDefinition, ListItemClickEvent } from '../../../models/columnDefinition';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { CompanyVoteProposalNameSearchBoxComponent } from '../company-vote-proposal-name-search-box/company-vote-proposal-name-search-box.component';
import { CompanyVoteProposalNameSearchFilter } from '../../../models/companyVoteProposalNameSearchFilter';
import { CompanyVoteProposalNameService } from '../../../services/company-vote-proposal-name.service';
import { CompanyVoteProposalNameDto } from '../../../models/api/companyVoteProposalNameDto';
import { Store } from '@ngrx/store';
import { IAppState } from '../../../store/states/app.state';
import { SetSearchFilter, SetPagination, SetSort } from '../../../store/actions/company-vote-proposal-name-list.actions';
import { selectCompanyVoteProposalNameListFullState } from '../../../store/selectors/company-vote-proposal-name-list.selectors';
import { take, filter } from 'rxjs/operators';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-company-vote-proposal-name-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    GenericListComponent,
    NgxSpinnerModule,
    CompanyVoteProposalNameSearchBoxComponent
  ],
  templateUrl: './company-vote-proposal-name-list.component.html',
  styleUrl: './company-vote-proposal-name-list.component.scss'
})
export class CompanyVoteProposalNameListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(GenericListComponent) genericList?: GenericListComponent<CompanyVoteProposalNameDto>;

  proposalNames: CompanyVoteProposalNameDto[] = [];
  totalItems = 0;
  isSearchVisible = true;

  currentPage = 1;
  pageSize = 10;
  sortBy = 'fullName';
  ascending = true;
  currentSearchFilter: CompanyVoteProposalNameSearchFilter = {};

  // Merge functionality state
  rightItem: CompanyVoteProposalNameDto | undefined = undefined;
  matchedItems: CompanyVoteProposalNameDto[] | undefined = undefined;

  showCompanies = false;

  private allColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'matchedYearsTooltip', displayName: '', dataType: 'tooltipIcon', sortable: false },
    { propertyName: 'fullName', displayName: 'Name' },
    { propertyName: 'companiesDisplay', displayName: 'Companies', dataType: 'list', listClickable: true },
    { propertyName: 'countryName', displayName: 'Country' },
    { propertyName: 'categoryName', displayName: 'Category' },
    { propertyName: 'subCategoryName', displayName: 'Subcategory' },
  ];

  get columnDefinitions(): ColumnDefinition[] {
    if (this.showCompanies) {
      return this.allColumnDefinitions;
    }
    return this.allColumnDefinitions.filter(c => c.propertyName !== 'companiesDisplay');
  }

  constructor(
    private router: Router,
    private toast: HotToastService,
    private spinnerService: NgxSpinnerService,
    private proposalNameService: CompanyVoteProposalNameService,
    private store: Store<IAppState>,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.store.select(selectCompanyVoteProposalNameListFullState).pipe(
      filter(state => state !== undefined && state !== null),
      take(1)
    ).subscribe(state => {
      this.currentSearchFilter = state.searchFilter;
      this.currentPage = state.currentPage;
      this.pageSize = state.pageSize;
      this.sortBy = state.sortBy;
      this.ascending = state.ascending;

      this.loadProposalNames();
    });
  }

  ngAfterViewInit(): void {}

  private loadProposalNames(): void {
    this.spinnerService.show();

    this.proposalNameService.search({
      ...this.currentSearchFilter,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (response) => {
        this.proposalNames = response.data.map(p => ({
          ...p,
          matchedYearsTooltip: p.matchedYears?.length && !p.matchedYears.includes(p.year)
            ? `Matched in ${p.matchedYears.join(', ')}`
            : undefined,
          // Same shape as the Large Shareholders list's Ownerships column: one line per company and
          // meeting year, with the country so the connection can be traced to the source files.
          companiesDisplay: (p.companies || [])
            .map(c => ({
              label: `${c.companyName}${c.countryName ? ` [${c.countryName}]` : ''} (${c.year})${c.proposalCount > 1 ? ` - ${c.proposalCount} proposals` : ''}`,
              companyId: c.companyId,
              year: c.year
            }))
        }));
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading shareholder proposal names:', error);
        this.toast.error('Error loading shareholder proposal names');
        this.spinnerService.hide();
      }
    });
  }

  async onCreateProposalName(): Promise<void> {
    try {
      await this.router.navigate(['/voting-results/shareholder-proposal-names/create']);
    } catch (error) {
      this.toast.error('Error navigating to create (route not found)');
    }
  }

  async onListItemClick(event: ListItemClickEvent<CompanyVoteProposalNameDto>): Promise<void> {
    if (event.column !== 'companiesDisplay' || !event.listItem?.companyId) {
      return;
    }
    // Navigate to the company's Voting Results tab for that meeting year, where the proposal is listed.
    try {
      await this.router.navigate(['/companies/edit', event.listItem.companyId], {
        queryParams: { tab: 'votingResults', year: event.listItem.year }
      });
    } catch (error) {
      this.toast.error('Error navigating to Company edit (route not found)');
    }
  }

  async onEditProposalName(proposalName: CompanyVoteProposalNameDto): Promise<void> {
    try {
      const year = proposalName.year ?? this.currentSearchFilter.year ?? Helpers.defaultMeasurementYear();
      await this.router.navigate(['/voting-results/shareholder-proposal-names/edit', proposalName.id], {
        queryParams: { year: year }
      });
    } catch (error) {
      this.toast.error('Error navigating to edit (route not found)');
    }
  }

  onOpenProposalNameInNewTab(proposalName: CompanyVoteProposalNameDto): void {
    const year = proposalName.year ?? this.currentSearchFilter.year ?? Helpers.defaultMeasurementYear();
    const urlTree = this.router.createUrlTree(['/voting-results/shareholder-proposal-names/edit', proposalName.id], {
      queryParams: { year: year }
    });
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank');
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.ascending = sort.direction === 'asc';

    this.store.dispatch(new SetSort({
      sortBy: this.sortBy,
      ascending: this.ascending
    }));

    this.loadProposalNames();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;

    this.store.dispatch(new SetPagination({
      currentPage: this.currentPage,
      pageSize: this.pageSize
    }));

    this.loadProposalNames();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.currentSearchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onSearchFilter(searchFilter: CompanyVoteProposalNameSearchFilter): void {
    this.currentSearchFilter = searchFilter;
    this.currentPage = 1;

    this.store.dispatch(new SetSearchFilter(searchFilter));

    if (this.genericList?.paginator) {
      this.genericList.paginator.pageIndex = 0;
    }

    this.loadProposalNames();
  }

  onExportToCsv(): void {
    this.spinnerService.show();

    this.proposalNameService.exportToCsv({
      ...this.currentSearchFilter,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `shareholder_proposal_names_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.toast.success('Shareholder proposal names exported successfully');
      },
      error: (error) => {
        console.error('Error exporting shareholder proposal names:', error);
        this.toast.error('Error exporting shareholder proposal names');
        this.spinnerService.hide();
      }
    });
  }

  onReadyToMatch(event: {rightItem?: CompanyVoteProposalNameDto, matchedItems?: CompanyVoteProposalNameDto[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onProposalNameCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select shareholder proposal names to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.fullName).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.fullName}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalProposalNames = this.proposalNames;
        const originalTotalItems = this.totalItems;

        // 1. Optimistically remove merged items from local state
        this.proposalNames = this.proposalNames.filter(p => !bulkMergeRequest.leftIds.includes(p.id));
        this.totalItems = this.totalItems - bulkMergeRequest.leftIds.length;

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.proposalNameService.bulkMerge(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Shareholder proposal names merged successfully');
            // 3. On success: Clear the generic list's internal matching state and reload the list
            this.genericList?.clearMatchingState();
            this.loadProposalNames();
          },
          error: (error) => {
            console.error('Error merging shareholder proposal names:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original lists
            this.proposalNames = originalProposalNames;
            this.totalItems = originalTotalItems;
            // Restore selection state for retry
            this.rightItem = originalProposalNames.find(p => p.id === bulkMergeRequest.rightId);
            this.matchedItems = originalProposalNames.filter(p => bulkMergeRequest.leftIds.includes(p.id));
            this.toast.error(`<strong>Failed to merge Shareholder Proposal Names:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onDeleteProposalName(proposalName: CompanyVoteProposalNameDto): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(proposalName.fullName)
      .subscribe(result => {
        if (result) {
          const originalProposalNames = this.proposalNames;
          const originalTotalItems = this.totalItems;

          this.proposalNames = this.proposalNames.filter(p => p.id !== proposalName.id);
          this.totalItems = this.totalItems - 1;

          this.spinnerService.show();
          this.proposalNameService.delete(proposalName.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Shareholder proposal name deleted successfully');
              this.loadProposalNames();
            },
            error: (error) => {
              console.error('Error deleting shareholder proposal name:', error);
              this.spinnerService.hide();
              this.proposalNames = originalProposalNames;
              this.totalItems = originalTotalItems;
              this.toast.error(`<strong>Failed to delete Shareholder Proposal Name:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  ngOnDestroy(): void {}
}
